import type { MouseEvent } from 'react';
import { useMemo, useState } from 'react';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Publish, PublishedWithChanges, CheckCircleOutlined, ErrorOutline } from '@mui/icons-material';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import { useCreateSessionModeMutation } from '@/state/mutations/sessions/use-create-mode.ts';
import { usePublishBatchMutation } from '@/state/mutations/events/use-publish-batch';
import { PUBLICATION_ITEM_TYPE } from '@/types/dtos/events/request/batch-publish.dto';
import type { ServerError } from '@/types/errors/server.error';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useEventStore } from '@/state/stores/use-event-store';
import { getObjectId } from '@/helpers/object.id.helper';
import { SESSION_PARENT_ENTITY } from '@/types/dtos/sessions/session-parent-entity.enum';
import { PUBLICATION_STATE_QUERY_KEY, usePublicationStateQuery } from '@/state/queries/events/use-publication-state';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { useQueryClient } from '@tanstack/react-query';
import { sessionBatchOrganizeChildOutOfSync } from '@/lib/publication-batch-staleness';

export const SessionPublish = ({
  session,
  indicativeOnly = false,
  onIndicativeClick,
  onPublishSuccess,
  /** PLAN event id (or any id in the origin group) for batch publish + publication-state; org sessions omit */
  batchEventId: batchEventIdProp,
}: {
  session: SessionDto;
  indicativeOnly?: boolean;
  onIndicativeClick?: () => void;
  /** Called after a successful publish (ORGANIZE mode create); e.g. keep flow schedule tab active */
  onPublishSuccess?: () => void;
  batchEventId?: string;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const queryClient = useQueryClient();
  const { data: permissions } = usePermissionsQuery();
  const [isOpen, setIsOpen] = useState(false);
  const { mutateAsync: createMode, isPending: isLegacyPending } = useCreateSessionModeMutation();
  const { mutateAsync: publishBatch, isPending: isBatchPending } = usePublishBatchMutation();
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventPlan = useEventStore((s) => s.eventPlan);

  const sessionForPublishState = useMemo(() => {
    if (session.mode === 'PLAN') return session;
    const originId = session.origin ? getObjectId(session.origin) : undefined;
    if (!originId) return session;
    return (
      eventSessionsPlan?.find((planSession) => {
        const planOriginId = planSession.origin ? getObjectId(planSession.origin) : planSession.id;
        return planOriginId === originId || planSession.id === originId;
      }) ?? session
    );
  }, [eventSessionsPlan, session]);

  const organizePublished = useMemo(() => {
    return sessionForPublishState.publishedModes?.find((pm) => pm.mode === 'ORGANIZE');
  }, [sessionForPublishState]);

  const isEventParented = session.parentType?.toLowerCase() === SESSION_PARENT_ENTITY.EVENT;

  const rawBatchEventId =
    batchEventIdProp ?? eventPlan?.id ?? (isEventParented ? getObjectId(session.parent) : undefined);

  const useBatchPublishPath = isEventParented && Boolean(rawBatchEventId);

  const canUpdateBatchEvent =
    rawBatchEventId != null &&
    rawBatchEventId !== '' &&
    Boolean(permissions?.events?.[rawBatchEventId]?.includes('update'));

  const publicationQuery = usePublicationStateQuery({
    eventId: useBatchPublishPath && rawBatchEventId ? rawBatchEventId : undefined,
    mode: 'ORGANIZE',
    enabled: useBatchPublishPath && canUpdateBatchEvent,
  });

  const planSessionId = sessionForPublishState.id;

  const publicationRecord = useMemo(() => {
    if (!publicationQuery.data) return undefined;
    return publicationQuery.data.items.find(
      (i) => i.itemType === PUBLICATION_ITEM_TYPE.SESSION_BASIC && i.scopeKey === planSessionId,
    );
  }, [publicationQuery.data, planSessionId]);

  const batchPublicationReady = useBatchPublishPath && canUpdateBatchEvent && publicationQuery.isPublicationStateReady;

  const organizeChildOutOfSync = useMemo(
    () =>
      sessionBatchOrganizeChildOutOfSync({
        useBatchPath: useBatchPublishPath,
        canUpdateEvent: canUpdateBatchEvent,
        isPublicationStateLoading: publicationQuery.isPublicationStateLoading,
        isError: publicationQuery.isError,
        isPublicationStateReady: publicationQuery.isPublicationStateReady,
        recordIsUpToDate: publicationRecord?.isUpToDate,
        legacyOutOfSync: !organizePublished || organizePublished.version !== sessionForPublishState.version,
      }),
    [
      useBatchPublishPath,
      canUpdateBatchEvent,
      publicationQuery.isPublicationStateLoading,
      publicationQuery.isError,
      publicationQuery.isPublicationStateReady,
      publicationRecord?.isUpToDate,
      organizePublished,
      sessionForPublishState.version,
    ],
  );

  const hasBeenPublished = useMemo(() => {
    if (useBatchPublishPath && canUpdateBatchEvent) {
      if (!batchPublicationReady) {
        return false;
      }
      return Boolean(publicationRecord);
    }
    return Boolean(organizePublished);
  }, [useBatchPublishPath, canUpdateBatchEvent, batchPublicationReady, publicationRecord, organizePublished]);

  const mode = useEventMode();
  const isIndicativeOnly = indicativeOnly || isOrganizeMode(mode);
  const isPending = useBatchPublishPath ? isBatchPending : isLegacyPending;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isIndicativeOnly) {
      onIndicativeClick?.();
      return;
    }
    if (useBatchPublishPath && canUpdateBatchEvent && publicationQuery.isError && rawBatchEventId) {
      void queryClient.invalidateQueries({ queryKey: [PUBLICATION_STATE_QUERY_KEY, rawBatchEventId, 'ORGANIZE'] });
      return;
    }
    if (useBatchPublishPath && canUpdateBatchEvent && publicationQuery.isPublicationStateLoading) {
      return;
    }
    setIsOpen(true);
  };

  const handleIndicativeClick = (event: MouseEvent<HTMLSpanElement>) => {
    if (!isIndicativeOnly) return;
    event.stopPropagation();
    onIndicativeClick?.();
  };

  const handleCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsOpen(false);
  };

  const handleConfirm = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      const sessionIdForPublish = session.mode !== 'PLAN' && session.origin ? getObjectId(session.origin) : session.id;

      if (useBatchPublishPath && rawBatchEventId) {
        const response = await publishBatch({
          eventId: rawBatchEventId,
          body: {
            mode: 'ORGANIZE',
            items: [{ itemType: PUBLICATION_ITEM_TYPE.SESSION_BASIC, sessionId: sessionIdForPublish }],
          },
        });
        const failed = response.results.filter((r) => r.status === 'failed');
        if (failed.length > 0) {
          const msg =
            failed
              .map((f) => f.errorMessage)
              .filter(Boolean)
              .join('; ') || 'Publication failed';
          throw new Error(msg);
        }
      } else if (isEventParented) {
        throw new Error('Batch publish path required for event-parented sessions but event ID could not be resolved');
      } else {
        await createMode({ id: sessionIdForPublish, mode: 'ORGANIZE' });
      }

      notification.notifySuccess(t('session.notification.updateSuccess', { title: session.title }));
      setIsOpen(false);
      onPublishSuccess?.();
    } catch (error) {
      const errorMessage = t('session.notification.updateError');
      const serverErrorMessage = (error as ServerError).message ?? (error instanceof Error ? error.message : undefined);
      const errorExplanation = serverErrorMessage ? `: ${serverErrorMessage}` : '';
      notification.notifyError(`${errorMessage}${errorExplanation}`);
    }
  };

  const batchPublicationPhase =
    useBatchPublishPath && canUpdateBatchEvent
      ? publicationQuery.isError
        ? ('error' as const)
        : publicationQuery.isPublicationStateLoading
          ? ('loading' as const)
          : batchPublicationReady
            ? ('ready' as const)
            : ('loading' as const)
      : null;

  const tooltipTitle =
    batchPublicationPhase === 'loading'
      ? t('common.loading')
      : batchPublicationPhase === 'error'
        ? t('common.publicationStatusError')
        : isIndicativeOnly
          ? organizeChildOutOfSync
            ? t('common.unpublishedUpdates')
            : t('common.upToDate')
          : !hasBeenPublished
            ? t('common.publish')
            : organizeChildOutOfSync
              ? t('common.rePublish')
              : t('common.upToDate');

  const renderIcon = () => {
    if (batchPublicationPhase === 'loading') {
      return <CircularProgress size={16} thickness={5} sx={{ color: 'text.secondary' }} />;
    }
    if (batchPublicationPhase === 'error') {
      return <ErrorOutline color="error" fontSize="small" />;
    }
    if (!organizeChildOutOfSync) return <CheckCircleOutlined sx={{ color: 'success.main' }} fontSize="small" />;
    if (hasBeenPublished) return <PublishedWithChanges fontSize="small" />;
    return <Publish fontSize="small" />;
  };

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span style={{ display: 'inline-block' }} onClick={handleIndicativeClick}>
          <IconButton
            aria-label="publish"
            color="primary"
            disabled={
              isIndicativeOnly ||
              (batchPublicationPhase === 'loading' || batchPublicationPhase === 'error'
                ? false
                : !organizeChildOutOfSync)
            }
            size="small"
            onClick={handleClick}
            loading={isPending}
            sx={{ width: '32px', height: '32px' }}
          >
            {renderIcon()}
          </IconButton>
        </span>
      </Tooltip>
      <ConfirmationModal
        open={isOpen}
        isLoading={isPending}
        title={t('session.publishModal.title')}
        text={t('session.publishModal.text', { title: session.title })}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default SessionPublish;
